import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";

const STATUSPAGE_URL = "https://outfall.statuspage.io";

export default function Incidents() {
  const { id } = useParams();

  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadIncident();
  }, [id]);

  async function loadIncident() {
    try {
      const res = await fetch(`${STATUSPAGE_URL}/api/v2/incidents.json`);
      const data = await res.json();

      const found = (data.incidents || []).find((item) => item.id === id);
      setIncident(found || null);
    } catch (err) {
      console.error("Failed to load incident:", err);
    }

    setLoading(false);
  }

  function formatStatus(status) {
    return status?.replaceAll("_", " ") || "unknown";
  }

  function getStatusColour(status) {
    if (status === "operational" || status === "none") return "#00875a";
    if (status === "degraded_performance" || status === "minor")
      return "#ffab00";
    if (status === "partial_outage") return "#ff991f";
    if (status === "major_outage" || status === "major") return "#de350b";
    if (status === "critical") return "#bf2600";
    if (status === "under_maintenance" || status === "maintenance")
      return "#6554c0";
    return "#42526e";
  }

  const sortedUpdates = useMemo(() => {
    return [...(incident?.incident_updates || [])].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  }, [incident]);

  if (loading) {
    return (
      <main style={styles.page}>
        <Header />
        <div style={styles.container}>
          <p style={styles.loading}>Loading incident...</p>
        </div>
      </main>
    );
  }

  if (!incident) {
    return (
      <main style={styles.page}>
        <Header />

        <div style={styles.container}>
          <h1 style={styles.title}>Incident not found</h1>

          <Link to="/status" style={styles.backLink}>
            ← Current Status
          </Link>
        </div>
      </main>
    );
  }

  const titleColour = getStatusColour(incident.impact);

  return (
    <main style={styles.page}>
      <Header />

      <div style={styles.container}>
        <header style={styles.incidentHeader}>
          <h1 style={{ ...styles.title, color: titleColour }}>
            {incident.name}
          </h1>
        </header>

        <section style={styles.timeline}>
          {sortedUpdates.map((update) => (
            <article key={update.id} style={styles.timelineRow}>
              <div
                style={{
                  ...styles.timelineStatus,
                  color: getStatusColour(update.status),
                }}
              >
                {formatStatus(update.status)}
              </div>

              <div style={styles.timelineContent}>
                <p style={styles.updateBody}>{update.body}</p>

                <p style={styles.updateMeta}>
                  Posted{" "}
                  {new Date(update.created_at).toLocaleString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    timeZoneName: "short",
                  })}
                </p>
              </div>
            </article>
          ))}
        </section>

        {incident.components?.length > 0 && (
          <section style={styles.affectedBox}>
            This incident affected:{" "}
            {incident.components.map((component, index) => (
              <span key={component.id}>
                {component.name}
                {index < incident.components.length - 1 ? ", " : "."}
              </span>
            ))}
          </section>
        )}

        <footer style={styles.footer}>
          <Link to="/status" style={styles.backLink}>
            ← Current Status
          </Link>

          <span style={styles.powered}>Powered by Atlassian Statuspage</span>
        </footer>
      </div>
    </main>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#ffffff",
    color: "#172b4d",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Helvetica Neue", Arial, sans-serif',
  },

  container: {
    width: "100%",
    maxWidth: "760px",
    margin: "0 auto",
    padding: "140px 16px 60px",
    boxSizing: "border-box",
  },

  loading: {
    fontSize: "16px",
    color: "#42526e",
  },

  incidentHeader: {
    textAlign: "center",
    marginBottom: "42px",
  },

  title: {
    margin: "0 0 14px",
    fontSize: "clamp(26px, 7vw, 34px)",
    lineHeight: "1.2",
    fontWeight: 400,
    overflowWrap: "break-word",
  },

  timeline: {
    display: "grid",
    gap: "28px",
  },

  timelineRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 135px) minmax(0, 1fr)",
    gap: "24px",
    alignItems: "start",
  },

  timelineStatus: {
    fontSize: "clamp(16px, 4vw, 20px)",
    lineHeight: "28px",
    fontWeight: 400,
    textTransform: "capitalize",
    overflowWrap: "break-word",
  },

  timelineContent: {
    minWidth: 0,
  },

  updateBody: {
    margin: 0,
    color: "#172b4d",
    fontSize: "16px",
    lineHeight: "24px",
    whiteSpace: "pre-line",
    overflowWrap: "break-word",
  },

  updateMeta: {
    margin: "7px 0 0",
    color: "#97a0af",
    fontSize: "14px",
    lineHeight: "20px",
    overflowWrap: "break-word",
  },

  affectedBox: {
    marginTop: "28px",
    border: "1px solid #dfe1e6",
    borderRadius: "4px",
    padding: "14px 16px",
    color: "#97a0af",
    fontSize: "14px",
    lineHeight: "22px",
    overflowWrap: "break-word",
  },

  footer: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "14px",
    marginTop: "50px",
    paddingTop: "15px",
    borderTop: "1px solid #dfe1e6",
  },

  backLink: {
    color: "#0052cc",
    textDecoration: "none",
    fontSize: "14px",
  },

  powered: {
    color: "#97a0af",
    fontSize: "14px",
  },
};
